'use client'

import { useState } from 'react'
import Link from 'next/link'
import { IconArrowRight } from '@tabler/icons-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { branding } from '@/lib/branding.config'
import { compliance } from '@/lib/compliance.config'
import { legalNotices } from '@/content/legal/notices'
import { getGroupedRegions } from '@/lib/data/gpu-products'
import { AIRGPU_OS_OPTIONS, AIRGPU_REGION_COUNT } from '@/lib/integrations/airgpu/types'
import { DocketHeader, DocketLabel, DocketRow, inkButton, lineButton } from './docket'

const joinList = (items: string[]) =>
  items.length > 1 ? `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}` : items.join('')

/** "Europe: Ireland, London, Frankfurt and Stockholm." — from the offered region table. */
const regionSentences = Object.entries(getGroupedRegions())
  .filter(([, regions]) => regions.length > 0)
  .map(([group, regions]) => {
    const names = regions.map((region) => {
      const match = region.name.match(/^(.*?)\s*\((.+)\)$/)
      return match && group.includes(match[1]) ? match[2] : region.name
    })
    return `${group}: ${joinList(names)}.`
  })
  .join(' ')

const osNames = joinList(Object.values(AIRGPU_OS_OPTIONS).map((os) => os.name))

// Billing, refund and timing answers are built from content/legal/notices.ts
// and lib/compliance.config.ts — the same statements and numbers as the Terms —
// so this FAQ cannot promise something the Terms do not.
const faqs = [
  {
    question: 'How does billing work?',
    answer: `${legalNotices.billingModel} ${legalNotices.chargingPeriod} ${legalNotices.chargingCadence}`,
  },
  {
    question: 'Do I need a subscription?',
    answer: `No. You add credit to a prepaid wallet and metered usage is deducted from it. ${legalNotices.startBalance}`,
  },
  {
    question: 'What happens when my balance runs out?',
    answer: legalNotices.zeroBalance,
  },
  {
    question: 'Can I get a refund?',
    answer: `Unused credit is refundable within ${compliance.refundWindowDays} days of the top-up. Compute that has already been used is not refundable, except for verified service-side faults reported within ${compliance.incidentReportDays} days. Send refund requests to ${compliance.contactEmail}.`,
  },
  {
    question: 'How long does a machine take to start?',
    answer: `Start-up takes minutes: a machine is usually ready within ${compliance.provisioningMinutes} minutes of being started, and its connection details appear on the machine page once it is running. Start-up time is billed.`,
  },
  {
    question: 'How do I connect to my machine?',
    answer: `Each machine runs ${osNames}. Connect with Remote Desktop using the address and password on the machine page, or install a compatible streaming client such as Parsec or Moonlight. Parsec and Moonlight are optional third-party applications that you obtain and use under their own terms; ${branding.name} does not supply or pre-install them.`,
  },
  {
    question: 'Where are the machines located?',
    answer: `In ${AIRGPU_REGION_COUNT} regions. ${regionSentences} You choose the region when you create a machine; not every GPU is offered in every region, and the hourly rate varies by region.`,
  },
  {
    question: 'Can I play any game?',
    answer: 'Most Windows games can be installed and played. Some multiplayer games use anti-cheat software that blocks cloud or virtual machines, so check the game before you add credit for it.',
  },
]

const pad = (n: number) => String(n).padStart(2, '0')

export function FaqSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0)

  return (
    <section aria-labelledby="faq-title">
      <DocketHeader fields={['REF 04', 'Questions on file', `${faqs.length} entries`, 'Answered from the Terms']} />

      <DocketRow
        rule={false}
        margin={
          <div>
            <DocketLabel index="04" code="FAQ-04 / INDEX" />
            <p
              aria-live="polite"
              className="mt-4 border-t border-foreground/20 pt-3 text-[11px] font-bold uppercase tracking-[0.14em] text-foreground"
            >
              {openIndex === null ? 'All entries closed' : `Reading Q.${pad(openIndex + 1)} of ${pad(faqs.length)}`}
            </p>
          </div>
        }
      >
        <h2
          id="faq-title"
          className="font-heading text-4xl font-bold uppercase leading-[0.95] tracking-[-0.02em] text-foreground md:text-5xl"
        >
          Questions, answered.
        </h2>
        <p className="mt-4 max-w-xl text-sm leading-relaxed text-foreground">
          Billing, refunds, start-up and connecting, in plain words and in the same terms as the
          Terms.
        </p>

        <div className="mt-8 border-t-2 border-foreground">
          {faqs.map((faq, idx) => {
            const isOpen = openIndex === idx
            const contentId = `faq-item-${idx}`

            return (
              <div key={faq.question} className="border-b border-foreground/20">
                <button
                  onClick={() => setOpenIndex(isOpen ? null : idx)}
                  aria-expanded={isOpen}
                  aria-controls={contentId}
                  className="grid w-full grid-cols-[3.5rem_minmax(0,1fr)_auto] items-center gap-3 py-4 text-left transition-colors hover:bg-secondary"
                >
                  <span
                    className={cn(
                      'px-1.5 py-1 text-center text-xs font-bold tabular-nums',
                      isOpen ? 'bg-foreground text-background' : 'text-foreground'
                    )}
                  >
                    Q.{pad(idx + 1)}
                  </span>
                  <span className="font-heading text-lg font-bold leading-snug text-foreground">{faq.question}</span>
                  <span aria-hidden className="pr-1 text-xs font-bold text-foreground">
                    {isOpen ? '[–]' : '[+]'}
                  </span>
                </button>
                <div
                  id={contentId}
                  className={cn(
                    'grid overflow-hidden transition-[grid-template-rows] duration-300 ease-out',
                    isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                  )}
                >
                  <div className="overflow-hidden">
                    <p className="max-w-2xl pb-6 pl-[4.25rem] pr-6 text-sm leading-relaxed text-foreground">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Button className={inkButton} asChild>
            <Link href="/faq">
              Read every answer
              <IconArrowRight className="h-4 w-4" />
            </Link>
          </Button>
          <Button variant="outline" className={lineButton} asChild>
            <Link href="/help">Contact support</Link>
          </Button>
        </div>
      </DocketRow>
    </section>
  )
}
